import { Text } from '@thanx/uikit/text'
import Select from 'components/Form/Select'
import { buildTranslate } from 'locales'
import {
  Fields as RedeemTemplateFields,
  RedeemType,
} from 'models/RedeemTemplate'
import React, { useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import {
  selectRedeemTemplate,
  selectRedeemTemplatesByState,
} from 'selectors/redeemTemplate'
import '../PromoCodes.scss'

const t = buildTranslate('promo_codes.create_edit.reward_section')

// Reward template types that the promo codes pipeline supports end-to-end
// (PRD REQ-03 + DATA-5203 for fixed_price). Other RedeemType values like
// `experience/hidden_menu`, `experience/golden_ticket`, and `points/static`
// are explicitly out of scope per the PRD's "Out of scope" list (Hidden
// Menu, Access Pass, Bonus Points) — the backend rejects them with 422
// and the merchant has no recourse from the wizard. Filter at the
// dropdown so they never appear as a selectable option.
const PROMO_CODE_SUPPORTED_TYPES: ReadonlyArray<RedeemType> = [
  'manual/amount',
  'manual/percent',
  'manual/item',
  'manual/fixed_price',
]

const isPromoCodeCompatible = (template: RedeemTemplateFields): boolean =>
  PROMO_CODE_SUPPORTED_TYPES.includes(template.type) &&
  Boolean(template.name?.trim())

type PromotionRewardSectionProps = {
  selectedTemplateId: number | null
  onTemplateSelect: (template: RedeemTemplateFields | null) => void
  rewardError?: string
}

type RewardDetailFieldProps = {
  label: string
  value: string
  className?: string
}

const RewardDetailField: React.FC<RewardDetailFieldProps> = ({
  label,
  value,
  className = 'mb-m',
}) => (
  <div className={className}>
    <Text.BodyText5 bold className="d-block mb-xxs">
      {label}
    </Text.BodyText5>
    <Text.BodyText4 className="grey-70">{value}</Text.BodyText4>
  </div>
)

type RewardVenueStatusProps = {
  venue: 'online' | 'instore'
  redemptionVenue: string
}

const RewardVenueStatus: React.FC<RewardVenueStatusProps> = ({
  venue,
  redemptionVenue,
}) => {
  const isEnabled = redemptionVenue === 'all' || redemptionVenue === venue
  return (
    <Text.BodyText4 className="grey-70">
      {t(`${venue}.${isEnabled ? 'enabled' : 'disabled'}`)}
    </Text.BodyText4>
  )
}

enum RewardDetailTab {
  Details = 'details',
  Online = 'online',
  Instore = 'instore',
}

const RewardSection: React.FC<PromotionRewardSectionProps> = ({
  selectedTemplateId,
  onTemplateSelect,
  rewardError,
}) => {
  const [showDetails, setShowDetails] = useState(false)
  const [activeTab, setActiveTab] = useState<RewardDetailTab>(
    RewardDetailTab.Details
  )
  // Stable id so the Select can link its aria-describedby to the error span
  // (matches the pattern used for `data-testid`).
  const rewardErrorId = 'reward-error'

  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({})
  const tabValues = Object.values(RewardDetailTab)

  const handleTabKeyDown = (e: React.KeyboardEvent) => {
    const currentIndex = tabValues.indexOf(activeTab)
    let nextIndex: number | null = null

    if (e.key === 'ArrowRight') {
      nextIndex = (currentIndex + 1) % tabValues.length
    } else if (e.key === 'ArrowLeft') {
      nextIndex = (currentIndex - 1 + tabValues.length) % tabValues.length
    }

    if (nextIndex !== null) {
      e.preventDefault()
      const nextTab = tabValues[nextIndex]
      setActiveTab(nextTab)
      tabRefs.current[nextTab]?.focus()
    }
  }

  const publishedTemplates = useSelector(state =>
    selectRedeemTemplatesByState(state, 'published')
  ).filter(isPromoCodeCompatible)

  // In edit mode the attached template may no longer be published — fetch it
  // from the full ORM set so it always appears in the dropdown. Only
  // include it when it's a promo-code-compatible type; otherwise the
  // wizard would offer to "edit" a promo whose configuration cannot be
  // saved (the legacy create path would have rejected it).
  const attachedTemplate = useSelector(state =>
    selectRedeemTemplate(state, selectedTemplateId)
  )
  const compatibleAttached =
    attachedTemplate && isPromoCodeCompatible(attachedTemplate)
      ? attachedTemplate
      : null

  const templates =
    compatibleAttached &&
    !publishedTemplates.some(t => t.id === compatibleAttached.id)
      ? [compatibleAttached, ...publishedTemplates]
      : publishedTemplates

  const selectedTemplate = templates.find(t => t.id === selectedTemplateId)

  const options = templates.map(template => ({
    value: template.id,
    label: template.name,
  }))

  // Source selectedOption from `options` so react-select can match it by
  // reference. Building a fresh `{value, label}` object here would render
  // the trigger correctly but leave the matching option in the open menu
  // un-highlighted, because react-select's default comparator is `===`.
  const selectedOption =
    options.find(o => o.value === selectedTemplateId) ?? null

  const handleSelect = (option: { value: number; label: string } | null) => {
    const template =
      option == null ? null : templates.find(t => t.id === option.value) || null
    onTemplateSelect(template)
    setShowDetails(false)
  }

  const rewardTypeNames: Record<string, string> = {
    item: t('types.item'),
    amount: t('types.amount'),
    percent: t('types.percent'),
    fixed_price: t('types.fixed_price'),
  }

  const getRewardTypeName = (type: string): string =>
    rewardTypeNames[type.split('/').pop()!] ?? type

  return (
    <div className="promo-section-card">
      <Text.Header4 className="mb-s">{t('title')}</Text.Header4>

      <Text.BodyText5 bold className="d-block mb-xs">
        {t('select_prompt')}
      </Text.BodyText5>
      <div className="d-flex align-items-center mb-s">
        <div className="flex-grow-1">
          <Select
            inputId="reward-template-select-input"
            options={options}
            value={selectedOption}
            onChange={handleSelect}
            placeholder={t('select_placeholder')}
            aria-label={t('select_prompt')}
            aria-invalid={Boolean(rewardError)}
            aria-describedby={rewardError ? rewardErrorId : undefined}
            isClearable
            // The Promo Codes form has a sticky FormNavbar at z-index 100
            // (PromoCodes.scss). Without portalling the menu, the default
            // react-select menu (z-index ~1) is painted underneath the
            // navbar whenever the page is scrolled. Render the menu into
            // document.body and bump zIndex past the navbar so it floats
            // cleanly. Scoped to this Select instance — the shared
            // components/Form/Select wrapper is consumed by Campaign
            // Builder, MerchantUsers role pickers, and Formsy wrappers
            // that have no sticky-navbar conflict; portalling globally
            // would risk subtle layout/scroll regressions there.
            menuPortalTarget={
              typeof document !== 'undefined' ? document.body : null
            }
            styles={{
              menuPortal: (base: Record<string, unknown>) => ({
                ...base,
                zIndex: 1000,
              }),
            }}
          />
        </div>
        {selectedTemplate && (
          <button
            type="button"
            onClick={() => setShowDetails(!showDetails)}
            className="promo-text-btn ml-m"
            aria-expanded={showDetails}
            aria-controls="reward-details-panel"
          >
            {showDetails ? t('hide_details') : t('view_details')}
          </button>
        )}
      </div>

      {rewardError && (
        <span
          id={rewardErrorId}
          role="alert"
          className="text-danger error-message font-size-14 d-block mb-s"
          data-testid="reward-error"
        >
          {rewardError}
        </span>
      )}

      <Text.BodyText5 className="grey-70">
        {t('create_new')}{' '}
        <Link to="/rewards" className="belize-hole-50">
          {t('create_link')}
        </Link>
      </Text.BodyText5>

      {showDetails && selectedTemplate && (
        <div
          id="reward-details-panel"
          className="mt-m border-top-1 grey-20-border pt-m"
        >
          {/* Tabs */}
          <div className="promo-detail-tabs" role="tablist">
            {tabValues.map(tab => (
              <button
                key={tab}
                ref={el => {
                  tabRefs.current[tab] = el
                }}
                type="button"
                role="tab"
                id={`reward-tab-${tab}`}
                aria-selected={activeTab === tab}
                aria-controls={`reward-tabpanel-${tab}`}
                tabIndex={activeTab === tab ? 0 : -1}
                onClick={() => setActiveTab(tab)}
                onKeyDown={handleTabKeyDown}
                className={`promo-detail-tab${
                  activeTab === tab ? ' promo-detail-tab--active' : ''
                }`}
              >
                {t(`tabs.${tab}`)}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div
            role="tabpanel"
            id={`reward-tabpanel-${activeTab}`}
            aria-labelledby={`reward-tab-${activeTab}`}
          >
            {activeTab === RewardDetailTab.Details && (
              <>
                <RewardDetailField
                  label={t('details.reward_type')}
                  value={getRewardTypeName(selectedTemplate.type)}
                />
                {selectedTemplate.discount !== null && (
                  <RewardDetailField
                    label={t('details.cost_to_business')}
                    value={`$${selectedTemplate.discount}`}
                  />
                )}
                {selectedTemplate.perceived_value !== null && (
                  <RewardDetailField
                    label={t('details.retail_price')}
                    value={`$${selectedTemplate.perceived_value}`}
                  />
                )}
                <RewardDetailField
                  label={t('details.number_of_uses')}
                  value={
                    selectedTemplate.multi_use_settings
                      ? t('details.multi_use')
                      : t('details.single_use')
                  }
                  className=""
                />
              </>
            )}
            {activeTab === RewardDetailTab.Online && (
              <RewardVenueStatus
                venue="online"
                redemptionVenue={selectedTemplate.redemption_venue}
              />
            )}
            {activeTab === RewardDetailTab.Instore && (
              <RewardVenueStatus
                venue="instore"
                redemptionVenue={selectedTemplate.redemption_venue}
              />
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default RewardSection
